import { useState } from 'react';
import { Layout } from '../../components/Layout/Layout';
import type { KeyboardEvent } from 'react';
import { useUser } from '../../app/UserContext';
import { getExercisesByCategory } from '../../domain/scoring/exerciseCatalog';
import { AdminPanel } from './AdminPanel';
import { NotificationSettings } from '../notifications/NotificationSettings';
import { CATEGORY_ORDER, CATEGORY_LABELS } from '../../domain/scoring/categories';
import { benchmarkRepsForBodyWeight } from '../../domain/scoring/pullupBenchmarks';
import type { Category, ExerciseConfig } from '../../domain/scoring/types';
import type { ScoringSex } from '../../domain/users/types';
import './SettingsPage.css';

/** Benchmark reps can be fractional after interpolation; show at most one decimal. */
function formatReps(reps: number): string {
  return Number.isInteger(reps) ? String(reps) : reps.toFixed(1);
}

export function SettingsPage() {
  const { user, updateProfile, logout } = useUser();

  // Edit buffers are seeded when editing starts rather than synced from `user`
  // by an effect, so there is no cascading setState during render.
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState('');

  const [editingWeight, setEditingWeight] = useState(false);
  const [weightVal, setWeightVal] = useState('');

  const [isReloading, setIsReloading] = useState(false);

  if (!user) return null;

  const startEditingName = () => {
    setNameVal(user.displayName);
    setEditingName(true);
  };

  const startEditingWeight = () => {
    setWeightVal(String(user.bodyWeightLb));
    setEditingWeight(true);
  };

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const newName = nameVal.trim() || 'Anonymous';
      void updateProfile({ displayName: newName });
      setEditingName(false);
      setNameVal(newName);
    } else if (e.key === 'Escape') {
      setEditingName(false);
      setNameVal(user.displayName);
    }
  };

  const handleWeightKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const newWeight = parseInt(weightVal, 10);
      if (!isNaN(newWeight) && newWeight > 0) {
        void updateProfile({ bodyWeightLb: newWeight });
        setEditingWeight(false);
        setWeightVal(String(newWeight));
      } else {
        setWeightVal(String(user.bodyWeightLb));
        setEditingWeight(false);
      }
    } else if (e.key === 'Escape') {
      setEditingWeight(false);
      setWeightVal(String(user.bodyWeightLb));
    }
  };

  const handleSexToggle = () => {
    const newSex: ScoringSex = user.scoringSex === 'male' ? 'female' : 'male';
    void updateProfile({ scoringSex: newSex });
  };

  const handleForceReload = async () => {
    setIsReloading(true);
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const name of cacheNames) {
          await caches.delete(name);
        }
      }
    } catch (err) {
      console.error('Error clearing caches:', err);
    } finally {
      window.location.href = '/log?refresh=' + Date.now();
    }
  };

  const categories: Category[] = [...CATEGORY_ORDER];
  // Active only: this is the reference list of what you can log, so a retired
  // exercise should not appear here even though its config is kept for old lifts.
  const exercisesByCategory = Object.fromEntries(
    categories.map((cat) => [cat, getExercisesByCategory(cat).filter((ex) => ex.isActive)]),
  );

  // Personalised pull-up numbers for the signed-in athlete.
  const benchReps = benchmarkRepsForBodyWeight(user.bodyWeightLb, user.scoringSex);

  return (
    <Layout>
      <div className="settings-page">
        <h1 className="settings-page__header">Settings</h1>

        <section className="settings-section">
          <h2 className="settings-section__title">Profile</h2>
          <div className="profile-grid">
            <div className="profile-field">
              <span className="profile-field__label">Display Name</span>
              {editingName ? (
                <div className="profile-field__input-container">
                  <input
                    type="text"
                    className="profile-field__input"
                    value={nameVal}
                    onChange={(e) => setNameVal(e.target.value)}
                    onKeyDown={handleNameKeyDown}
                    onBlur={() => {
                      setEditingName(false);
                      setNameVal(user.displayName);
                    }}
                    autoFocus
                    aria-label="Edit display name"
                  />
                </div>
              ) : (
                <button
                  className="profile-field__toggle"
                  onClick={startEditingName}
                  aria-label="Tap to edit display name"
                >
                  {user.displayName}
                </button>
              )}
              {editingName && <div className="profile-field__edit-hint">Press Enter to save</div>}
            </div>

            <div className="profile-field">
              <span className="profile-field__label">Email</span>
              <span className="profile-field__value profile-field__value--muted">
                {user.normalizedEmail}
              </span>
            </div>

            <div className="profile-field">
              <span className="profile-field__label">Body Weight (lb)</span>
              {editingWeight ? (
                <div className="profile-field__input-container">
                  <input
                    type="number"
                    inputMode="numeric"
                    className="profile-field__input"
                    value={weightVal}
                    onChange={(e) => setWeightVal(e.target.value)}
                    onKeyDown={handleWeightKeyDown}
                    onBlur={() => {
                      setEditingWeight(false);
                      setWeightVal(String(user.bodyWeightLb));
                    }}
                    autoFocus
                    aria-label="Edit body weight"
                  />
                </div>
              ) : (
                <button
                  className="profile-field__toggle"
                  onClick={startEditingWeight}
                  aria-label="Tap to edit body weight"
                >
                  {user.bodyWeightLb} lb
                </button>
              )}
              {editingWeight && <div className="profile-field__edit-hint">Press Enter to save</div>}
            </div>

            <div className="profile-field">
              <span className="profile-field__label">Scoring Sex</span>
              <button
                className="profile-field__toggle"
                onClick={handleSexToggle}
                aria-label="Toggle scoring sex"
              >
                {user.scoringSex === 'male' ? 'Male' : 'Female'}
              </button>
            </div>
          </div>
        </section>

        <section className="settings-section">
          <h2 className="settings-section__title">Exercise Normalizations</h2>
          {Object.entries(exercisesByCategory).map(([cat, exercises]) => {
            const activeExercises = (exercises as ExerciseConfig[]).filter((ex) => ex.isActive);
            return (
              <div key={cat} className="exercise-category">
                <h3 className="exercise-category__title">{CATEGORY_LABELS[cat as Category]}</h3>
                <table className="exercise-table">
                  <thead>
                    <tr>
                      <th>Exercise</th>
                      <th>Multiplier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeExercises.map((ex) => (
                      <tr
                        key={ex.id}
                        className={ex.isCompetitionLift ? 'exercise-table__row--comp' : ''}
                      >
                        <td>
                          {ex.displayName}
                          {ex.isCompetitionLift && (
                            <span
                              className="exercise-table__comp-star"
                              aria-label="Competition Lift"
                            >
                              ★
                            </span>
                          )}
                          {ex.inputWeightMultiplier > 1 && (
                            <span
                              className="exercise-table__db-badge"
                              aria-label="Dumbbell (doubles input weight)"
                            >
                              DB
                            </span>
                          )}
                        </td>
                        <td className="exercise-table__multiplier">
                          {ex.normalizationMultiplier.toFixed(2)}×
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </section>

        <section className="settings-section">
          <h2 className="settings-section__title">About Scoring</h2>
          <p className="text-muted">
            Your score uses a multi-step formula that accounts for body weight and makes all
            exercises comparable:
          </p>
          <ol className="settings-page__scoring-steps">
            <li>
              <strong>Estimate 1RM</strong> — Your weight × reps is converted to an estimated
              one-rep max using the Epley formula.
            </li>
            <li>
              <strong>Normalize</strong> — If you used an alternative exercise (e.g. incline bench),
              the 1RM is multiplied by its normalization factor to estimate the equivalent
              competition lift 1RM.
            </li>
            <li>
              <strong>DOTS Score</strong> — The normalized 1RM is scored using a DOTS-derived
              formula that adjusts for your body weight and scoring sex, producing your final
              points.
            </li>
          </ol>
          <div className="text-example">
            Example: 185 lb bench × 5 reps → ~208 lb estimated 1RM → 208 × 1.00 (comp lift) → DOTS
            score → 61 points
          </div>
          <p className="text-muted" style={{ marginTop: '8px' }}>
            Your monthly total is the sum of your best score in each category (Push + Pull + Hinge +
            Squat). Dumbbell exercises automatically double the entered weight. Competition lifts
            (★) are the 1.00× baseline.
          </p>
          <p className="text-muted" style={{ marginTop: '8px' }}>
            <strong>Female Push Parity:</strong> Because Push is scored as an isolated category
            (rather than as part of a 3-lift total sum), female Push includes a 1.14× category
            adjustment factor. This accounts for upper-body powerlifting averages and ensures female
            pressing earns equal points for equal relative effort alongside Squat and Pull.
          </p>
        </section>

        <section className="settings-section">
          <h2 className="settings-section__title">How Pull-ups Are Scored</h2>

          <div className="pullup-target">
            <span className="pullup-target__label">Your 100-point target</span>
            <span className="pullup-target__value">
              {formatReps(benchReps)} strict pull-ups at {user.bodyWeightLb} lb
            </span>
          </div>

          <p className="text-muted">
            Your body weight is the resistance, so pull-ups skip the DOTS body-weight adjustment —
            applying it would count body weight twice.
          </p>
          <p className="text-muted" style={{ marginTop: '8px' }}>
            Added weight stacks on top of your body weight, so weighted reps are how you score above
            100.
          </p>
        </section>

        <section className="settings-section">
          <h2 className="settings-section__title">Notifications</h2>
          <NotificationSettings />
        </section>

        <section className="settings-section">
          <h2 className="settings-section__title">App Maintenance</h2>
          <p className="text-muted" style={{ marginBottom: '12px' }}>
            If you experience caching issues, un-synced scores, or missed updates, force clear the
            app cache and reload.
          </p>
          <button
            className="pwa-button reload-button"
            onClick={handleForceReload}
            disabled={isReloading}
            aria-label="Force Reload App"
          >
            {isReloading ? '⏳ Clearing Cache & Reloading...' : '🔄 Force Reload Web App'}
          </button>
        </section>

        <section className="settings-section">
          <h2 className="settings-section__title">Admin</h2>
          <p className="text-muted" style={{ marginBottom: '12px' }}>
            Everyone&rsquo;s profiles, the exercise multipliers, and every lift ever logged.
            Deletions are immediate and cannot be undone.
          </p>
          <AdminPanel />
        </section>

        <button className="logout-button" onClick={logout} aria-label="Log Out">
          Log Out
        </button>
      </div>
    </Layout>
  );
}
